import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, UserPlus } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { isApiError } from '@/lib/api/errors'
import { useSaveMember } from '../api'
import { DISCIPLINES, type Project, type TeamMember } from '../types'
import { toastError, useStaffLookup } from '../ui'

type Props = { open: boolean; project: Project; member: TeamMember | null; onOpenChange: (v: boolean) => void }

export function MemberFormModal({ open, project, member, onOpenChange }: Props) {
  return open ? <MemberForm key={member?.id ?? 'new'} project={project} member={member} onDone={() => onOpenChange(false)} /> : null
}

function MemberForm({ project, member, onDone }: { project: Project; member: TeamMember | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveMember(project.id)
  const { staff, byId } = useStaffLookup()

  const schema = z.object({
    userId: z.string().min(1, t('projects.team.form.userRequired')),
    discipline: z.enum(DISCIPLINES),
    title: z.string().trim().min(2, t('projects.team.form.titleRequired')).max(60),
  })

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { userId: member?.userId ?? '', discipline: member?.discipline ?? 'frontend', title: member?.title ?? '' },
  })
  const { errors } = form.formState
  const taken = new Set(project.team.filter((m) => m.id !== member?.id).map((m) => m.userId))

  const onSubmit = form.handleSubmit(async (input) => {
    try {
      await save.mutateAsync({ id: member?.userId, input })
      toast.success(t(member ? 'projects.team.updated' : 'projects.team.assigned'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'PROJECT_MEMBER_EXISTS') form.setError('userId', { message: t('errors.PROJECT_MEMBER_EXISTS') })
      else toastError(e)
    }
  })

  const onUserChange = (id: string) => {
    if (!form.getValues('title')) form.setValue('title', byId.get(id)?.position ?? '')
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<UserPlus />}
      title={t(member ? 'projects.team.form.editTitle' : 'projects.team.form.createTitle')}
      description={t('projects.team.form.hint', { project: project.name })}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="mem-user">{t('projects.team.form.user')}</Label>
          <FormSelect
            id="mem-user"
            control={form.control}
            name="userId"
            disabled={!!member}
            placeholder={t('common.select')}
            onValueChange={onUserChange}
            options={staff.map((u) => ({ value: u.id, label: u.name, description: u.position, disabled: taken.has(u.id) }))}
          />
          {errors.userId ? <p className="text-xs text-destructive">{errors.userId.message}</p> : null}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="mem-disc">{t('projects.team.form.discipline')}</Label>
            <FormSelect id="mem-disc" control={form.control} name="discipline" options={DISCIPLINES.map((d) => ({ value: d, label: t(`projects.discipline.${d}`) }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mem-title">{t('projects.team.form.title')}</Label>
            <Input id="mem-title" aria-invalid={!!errors.title} {...form.register('title')} />
            {errors.title ? <p className="text-xs text-destructive">{errors.title.message}</p> : null}
          </div>
        </div>
      </div>
    </Modal>
  )
}
